import { useEffect, useMemo, useState } from "react";
import { Layers } from "lucide-react";
import { api } from "../lib/api";
import { MonthlyInvoiceCard } from "./MonthlyInvoiceCard";

const BASE = ["هنجرستيشن", "كيتا طلبات", "كيتا شرائح", "نينجا"];
const ALIAS = new Set(["كيتا", "ننجا", "نينجا", "هنقرستيشن", "هنجر"]);
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const COLS = [["completed_orders", "الطلبات"], ["amount_excl_vat", "قبل الضريبة"], ["vat", "الضريبة"], ["amount_incl_vat", "شامل الضريبة"], ["total_deduction", "الخصومات"], ["net_ftr", "الصافي"]];

const TotalsView = ({ platforms }) => {
  const [all, setAll] = useState([]);
  const [month, setMonth] = useState("");
  useEffect(() => { api.listMonthlyInvoices().then((l) => { setAll(l); setMonth((m) => m || l[0]?.month || ""); }).catch(() => {}); }, []);
  const months = useMemo(() => [...new Set(all.map((d) => d.month).filter(Boolean))].sort().reverse(), [all]);
  const rows = platforms.map((p) => ({ p, s: all.find((d) => d.month === month && (d.platform || "هنجرستيشن") === p)?.summary }));
  const tot = Object.fromEntries(COLS.map(([k]) => [k, rows.reduce((a, r) => a + Number(r.s?.[k] || 0), 0)]));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="invoices-totals">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Layers className="h-5 w-5 text-indigo-600" /> إجمالي فواتير المنصات</h3>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="invoices-totals-month" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-right text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="p-2">المنصة</th>{COLS.map(([k, l]) => <th key={k} className="p-2">{l}</th>)}</tr></thead>
          <tbody>
            {rows.map(({ p, s }) => (
              <tr key={p} className="border-b" data-testid={`invoices-totals-row-${p}`}>
                <td className="p-2 font-semibold">{p}</td>
                {COLS.map(([k]) => <td key={k} className={`p-2 tabular-nums ${s ? "" : "text-slate-300"}`}>{s ? fmt(s[k]) : "—"}</td>)}
              </tr>
            ))}
            <tr className="bg-indigo-50 font-bold text-indigo-900" data-testid="invoices-totals-sum"><td className="p-2">الإجمالي</td>{COLS.map(([k]) => <td key={k} className="p-2 tabular-nums">{fmt(tot[k])}</td>)}</tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const InvoicesHub = ({ canEdit }) => {
  const [extra, setExtra] = useState([]);
  const [tab, setTab] = useState("هنجرستيشن");
  useEffect(() => { api.revenueConfig().then((c) => setExtra((c?.platforms || []).filter((p) => !BASE.includes(p) && !ALIAS.has(p)))).catch(() => {}); }, []);
  const platforms = [...BASE, ...extra];
  const tabs = ["__total", ...platforms];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="invoices-platform-tabs">
        {tabs.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`invoices-tab-${t}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${tab === t ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300"}`}>
            {t === "__total" ? "الإجمالي" : t}
          </button>
        ))}
      </div>
      {tab === "__total" ? <TotalsView platforms={platforms} /> : <MonthlyInvoiceCard key={tab} canEdit={canEdit} platform={tab} />}
    </div>
  );
};
